import { useId, useState, type ReactNode } from "react";
import { HeartHandshake, Info, Users } from "lucide-react";
import { Button } from "../../../../ui/index.ts";
import overlayStyles from "../shared/offboarding-page-overlay.module.css";
import styles from "./support-request-banner.module.css";

export type SupportRequestChoice = "yes" | "no" | null;

export interface SupportRequestBannerProps {
    open: boolean;
    loading?: boolean;
    notice?: ReactNode;
    onSubmitSupport: () => void;
    onProceedResignation: () => void;
}

const RECIPIENTS = [
    { label: "Management", icon: Users },
   
] as const;

/** Page-scoped support request dialog (does not cover sidebar/header). */
export function SupportRequestBanner({
    open,
    loading = false,
    notice,
    onSubmitSupport,
    onProceedResignation,
}: SupportRequestBannerProps) {
    const [choice, setChoice] = useState<SupportRequestChoice>("yes");
    const titleId = useId();

    if (!open) return null;

    return (
      <div className={overlayStyles.overlay} role="presentation">
        <div className={styles.overlayStack}>
          {notice}

          <div
            className={styles.card}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.content}>
              <div className={styles.headerRow}>
                <div className={styles.iconCircle} aria-hidden>
                  <HeartHandshake size={22} strokeWidth={1.75} />
                </div>
                <div className={styles.headerText}>
                  <h2 id={titleId} className={styles.title}>
                    Is there anything we can do to support you?
                  </h2>
                  <p className={styles.subtitle}>
                    If you're open to a conversation, the concerned team is
                    available to discuss your feedback and explore potential
                    solutions before you proceed further.
                  </p>
                </div>
              </div>

              <div
                className={styles.options}
                role="radiogroup"
                aria-label="Support request choice"
              >
                <label className={styles.option}>
                  <input
                    type="radio"
                    name="support-choice"
                    value="yes"
                    checked={choice === "yes"}
                    onChange={() => setChoice("yes")}
                    className={styles.radioInput}
                  />
                  <span className={styles.optionLabel}>
                    Yes, I would like to discuss my concerns
                  </span>
                </label>

                <label className={styles.option}>
                  <input
                    type="radio"
                    name="support-choice"
                    value="no"
                    checked={choice === "no"}
                    onChange={() => setChoice("no")}
                    className={styles.radioInput}
                  />
                  <span className={styles.optionLabel}>
                    No, I do not need any assistance
                  </span>
                </label>
              </div>

              {choice === "yes" && (
                <div className={styles.infoPanel}>
                  <div className={styles.infoHeader}>
                    <Info size={16} strokeWidth={2} aria-hidden />
                    <span className={styles.infoText}>
                      An intervention request will be sent to:
                    </span>
                  </div>
                  <div className={styles.recipients}>
                    {RECIPIENTS.map(({ label, icon: Icon }) => (
                      <div key={label} className={styles.recipient}>
                        <Icon size={15} strokeWidth={1.75} aria-hidden />
                        <span>{label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className={styles.actions}>
                {choice === "yes" && (
                  <Button
                    variant="accent"
                    className={styles.primaryBtn}
                    onClick={onSubmitSupport}
                    disabled={loading}
                  >
                    Submit Support Request
                  </Button>
                )}
                {choice === "no" && (
                  <Button
                    variant="danger"
                    onClick={onProceedResignation}
                    disabled={loading}
                  >
                    Proceed
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
}
